import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import { toast } from 'react-toastify';
import { useAuth } from '../services/AuthService';

export default function InternalChat() {
  const { api } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/chat/conversations');
      const baseConversations = response.data || [];
      const withMessages = await Promise.all(baseConversations.map(async (conversation) => {
        try {
          const messages = await api.get(`/chat/conversations/${conversation.pedido_id}/messages`);
          return { ...conversation, messages: messages.data || [] };
        } catch (_) {
          return { ...conversation, messages: [] };
        }
      }));
      setConversations(withMessages);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar os clientes');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
    const interval = window.setInterval(load, 12000);
    return () => window.clearInterval(interval);
  }, [load]);

  const send = async (conversation) => {
    const message = String(drafts[conversation.pedido_id] || '').trim();
    if (!message) return;
    try {
      setSendingId(conversation.pedido_id);
      await api.post(`/chat/conversations/${conversation.pedido_id}/messages`, { message });
      setDrafts((current) => ({ ...current, [conversation.pedido_id]: '' }));
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nao foi possivel enviar a mensagem');
    } finally {
      setSendingId(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto' }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2, borderRadius: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center"><ChatIcon color="primary" /><Typography variant="h5" fontWeight={800}>Chat interno</Typography></Stack>
        <Typography color="text.secondary" sx={{ mt: 1 }}>Converse com os clientes que possuem pedidos em andamento.</Typography>
      </Paper>
      {loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 7 }}><CircularProgress /></Box> : conversations.length === 0 ? (
        <Alert severity="info">Nao ha pedidos ativos para contato no momento.</Alert>
      ) : <Stack spacing={2}>{conversations.map((conversation) => (
        <Paper key={conversation.pedido_id} sx={{ overflow: 'hidden', borderRadius: 3, border: '1px solid #e5e7eb' }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ p: 2, bgcolor: '#fff7ed', color: '#f97316', display: 'grid', placeItems: 'center' }}><PersonIcon /></Box>
            <Box sx={{ flex: 1, py: 1.5, pr: 2 }}><Typography fontWeight={800}>{conversation.customer_name || 'Cliente'}</Typography><Typography variant="body2" color="text.secondary">Pedido #{conversation.lanchonete_order_number || conversation.pedido_id} - {conversation.customer_phone_number || 'sem telefone'}</Typography></Box>
          </Stack>
          <Divider />
          <Box sx={{ p: 2, bgcolor: '#f8fafc', display: 'grid', gap: 1, maxHeight: 280, overflowY: 'auto' }}>
            {conversation.messages?.length ? conversation.messages.map((item) => (
              <Box key={item.id} sx={{ justifySelf: item.sender_type === 'cliente' ? 'start' : 'end', maxWidth: '82%', px: 1.5, py: 1, borderRadius: 2, bgcolor: item.sender_type === 'cliente' ? '#fff' : '#E50914', color: item.sender_type === 'cliente' ? '#1f2937' : '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.12)' }}>
                <Typography variant="body2">{item.message}</Typography><Typography variant="caption" sx={{ opacity: .72 }}>{item.sender_type === 'cliente' ? 'Cliente' : 'Voce'} - {new Date(item.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</Typography>
              </Box>
            )) : <Typography variant="body2" color="text.secondary">Ainda nao ha mensagens nesta conversa.</Typography>}
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ p: 1.5, borderTop: '1px solid #e5e7eb' }}>
            <TextField fullWidth size="small" label="Mensagem para o cliente" value={drafts[conversation.pedido_id] || ''} inputProps={{ maxLength: 1000 }} onChange={(event) => setDrafts((current) => ({ ...current, [conversation.pedido_id]: event.target.value }))} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send(conversation); } }} />
            <Button variant="contained" startIcon={<SendIcon />} disabled={sendingId === conversation.pedido_id} onClick={() => send(conversation)}>Enviar</Button>
          </Stack>
        </Paper>
      ))}</Stack>}
    </Box>
  );
}
